import { Box, Button, Dialog, DialogContent, DialogTitle, FormControlLabel, MenuItem, Slide, Stack, Switch, TextField, Typography } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { DatePicker } from "@mui/x-date-pickers";
import { useSnackbar } from "notistack";
import React, { ReactElement } from "react";
import { ApiError } from "../../Api/ApiError";
import { getChargeCategoryList, regStudioPackData } from "../../Api/Store";
import { WeekLongString, WeekType } from "../../Helper/Week";
import { ChargeCategory } from "../../Objects/ChargeCategory";
import { PackChargeType, PackChargeTypeStrings, StudioPack } from "../../Objects/StudioPack";
import { StoreObjectStore } from "../../ObjectStore/StoreObjectStore";
import LabelItem from "../atoms/LabelItem";
import TimeInput from "../atoms/TimeInput";

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />
})

export interface StudioPackEditDialogProps {
    open: boolean
    onClose: (isEdit: boolean) => void
    editStudioPack?: StudioPack
}

const StudioPackEditDialog: React.FC<StudioPackEditDialogProps> = ({
    open,
    onClose,
    editStudioPack
}) => {
    let { enqueueSnackbar } = useSnackbar()
    const { storeObjectState } = React.useContext(StoreObjectStore)

    const [chargeCategoryList, setChargeCategoryList] = React.useState<ChargeCategory[]>()

    const [dialogOpen, setDialogOpen] = React.useState<boolean>(false)
    const [studioPack, setStudioPack] = React.useState<StudioPack>({})

    const abortController = React.useMemo(() => new AbortController(), [])

    React.useEffect(() => {
        return () => {
            abortController.abort()
        }
    }, [abortController])

    const doSave = React.useCallback(() => {
        regStudioPackData({
            studioPack: {
                ...studioPack,
                pack_str_id: storeObjectState.storeData?.str_id
            }
        }, {signal: abortController.signal}).then(() => {
            onClose(true)
            enqueueSnackbar("変更しました", { variant: "success" })
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            }else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    }, [abortController, studioPack, storeObjectState.storeData?.str_id, onClose, enqueueSnackbar])

    React.useEffect(() => {
        const controller = new AbortController()
        getChargeCategoryList({
            "access_key": "HEAD-OFFICE_LOGIN"
        }, {signal: controller.signal}).then(result => {
            setChargeCategoryList(result.chargeCategoryList)
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            }else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
        return () => {
            controller.abort()
        }
    }, [enqueueSnackbar])

    React.useEffect(() => {
        if (editStudioPack) {
            setStudioPack(editStudioPack)
        } else {
            setStudioPack({})
        }
    }, [editStudioPack])

    React.useEffect(() => {
        if (open) {
            setDialogOpen(true)
        } else {
            setDialogOpen(false)
            setStudioPack({})
        }
    }, [open])

    return (
        <Dialog
            open={dialogOpen}
            TransitionComponent={Transition}
            onClose={() => {
                onClose(false)
            }}
            fullWidth
        >
            <DialogTitle>パック料金設定を変更</DialogTitle>
            <DialogContent
                sx={{
                    overflowY: "visible"
                }}
            >
                <Box
                    component="form"
                    onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                        e.preventDefault()
                    }}
                >
                    <Stack
                        spacing={0.5}
                    >
                        <LabelItem
                            title="パック料金名称"
                        >
                            <TextField
                                fullWidth
                                size="small"
                                value={studioPack.pack_name ? studioPack.pack_name : ""}
                                onChange={(e) => {
                                    let data: StudioPack = {
                                        ...studioPack,
                                        pack_name: e.target.value
                                    }
                                    setStudioPack(data)
                                }}
                            />
                        </LabelItem>
                        <LabelItem
                            title="適用曜日"
                        >
                            {Object.keys(WeekLongString).map(key => (
                                <FormControlLabel
                                    key={key}
                                    control={<Switch
                                        color="secondary"
                                        checked={studioPack.week_conditions ? studioPack.week_conditions.includes(key as WeekType) : false}
                                        onChange={e => {
                                            let wc: WeekType[] = studioPack.week_conditions ? studioPack.week_conditions.filter(value => value !== key as WeekType) : []
                                            if (e.target.checked) {
                                                wc.push(key as WeekType)
                                            }
                                            let sp: StudioPack = {
                                                ...studioPack,
                                                week_conditions: wc.length > 0 ? wc : undefined
                                            }
                                            setStudioPack(sp)
                                        }}
                                    />}
                                    label={WeekLongString[key as WeekType]}
                                />
                            ))}
                        </LabelItem>
                        <LabelItem
                            title="適用スタジオ"
                        >
                            <TextField
                                select
                                fullWidth
                                size="small"
                                value={studioPack.pack_std_id ? storeObjectState.studioList?.find(value => value.std_id === studioPack.pack_std_id)?.std_id : ""}
                                onChange={e => {
                                    if (e.target.value) {
                                        setStudioPack({
                                            ...studioPack,
                                            pack_std_id: parseInt(e.target.value),
                                        })
                                    } else {
                                        setStudioPack({
                                            ...studioPack,
                                            pack_std_id: undefined,
                                        })
                                    }
                                }}
                            >
                                <MenuItem value="" />
                                {storeObjectState.studioList?.map(studioData => (
                                    <MenuItem
                                        key={`${studioData.str_id}-${studioData.std_id}`}
                                        value={studioData.std_id}
                                    >{`${studioData.std_name}/${studioData.std_area}`}</MenuItem>
                                ))}
                            </TextField>
                        </LabelItem>
                        <LabelItem
                            title="適用予約タイミング"
                        >
                            {"予約日"}
                            <TextField
                                size="small"
                                sx={{ width: 50, verticalAlign: "middle" }}
                                type="number"
                                inputProps={{
                                    sx: {
                                        padding: 0.5
                                    }
                                }}
                                value={studioPack.pack_rvtiming_start ? studioPack.pack_rvtiming_start : ""}
                                onChange={(e) => {
                                    setStudioPack({
                                        ...studioPack,
                                        pack_rvtiming_start: Number(e.target.value)
                                    })
                                }}
                            />
                            {"日前から"}
                            <TextField
                                size="small"
                                sx={{ width: 50, verticalAlign: "middle" }}
                                type="number"
                                inputProps={{
                                    sx: {
                                        padding: 0.5
                                    }
                                }}
                                onChange={(e) => {
                                    setStudioPack({
                                        ...studioPack,
                                        pack_rvtiming_end: Number(e.target.value)
                                    })
                                }}
                            />
                            {"日前からの予約で適用"}
                        </LabelItem>
                        <LabelItem
                            title="設定適用期間"
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                            >
                                <Box
                                    sx={{
                                        display: "inline-block",
                                        verticalAlign: "middle"
                                    }}
                                >
                                    <DatePicker
                                        value={studioPack.pack_rvdate_start ? studioPack.pack_rvdate_start : null}
                                        onChange={value => {
                                            setStudioPack({
                                                ...studioPack,
                                                pack_rvdate_start: value ? value : undefined
                                            })
                                        }}
                                        renderInput={params => <TextField {...params} size="small" />}
                                        inputFormat="yyyy/MM/dd"
                                        mask="____/__/__"
                                        InputProps={{
                                            sx: {
                                                width: 150,
                                                verticalAlign: "middle"
                                            }
                                        }}
                                    />

                                </Box>
                                <Typography>～</Typography>
                                <Box
                                    sx={{
                                        display: "inline-block",
                                        verticalAlign: "middle"
                                    }}
                                >
                                    <DatePicker
                                        value={studioPack.pack_rvdate_end ? studioPack.pack_rvdate_end : null}
                                        onChange={value => {
                                            setStudioPack({
                                                ...studioPack,
                                                pack_rvdate_end: value ? value : undefined
                                            })
                                        }}
                                        renderInput={params => <TextField {...params} size="small" />}
                                        inputFormat="yyyy/MM/dd"
                                        mask="____/__/__"
                                        InputProps={{
                                            sx: {
                                                width: 150,
                                                verticalAlign: "middle"
                                            }
                                        }}
                                    />
                                </Box>
                            </Stack>
                        </LabelItem>
                        <LabelItem
                            title="設定適用時間帯"
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                            >
                                <TimeInput
                                    value={studioPack.pack_rvtime_start ? studioPack.pack_rvtime_start : null}
                                    onChange={newTime => {
                                        setStudioPack({
                                            ...studioPack,
                                            pack_rvtime_start: newTime ? newTime : undefined
                                        })
                                    }}
                                />
                                <Typography>～</Typography>
                                <TimeInput
                                    value={studioPack.pack_rvtime_end ? studioPack.pack_rvtime_end : null}
                                    onChange={newTime => {
                                        setStudioPack({
                                            ...studioPack,
                                            pack_rvtime_end: newTime ? newTime : undefined
                                        })
                                    }}
                                />
                            </Stack>
                        </LabelItem>
                        <LabelItem
                            title="適用コマ数"
                        >
                            <TextField
                                size="small"
                                sx={{ width: 50, verticalAlign: "middle" }}
                                type="number"
                                inputProps={{
                                    sx: {
                                        padding: 0.5
                                    }
                                }}
                                onChange={(e) => {
                                    setStudioPack({
                                        ...studioPack,
                                        pack_unit_length: Number(e.target.value)
                                    })
                                }}
                                value={studioPack.pack_unit_length ? studioPack.pack_unit_length : ""}
                            /> コマ
                        </LabelItem>
                        <LabelItem
                            title="適用料金カテゴリ"
                        >
                            <TextField
                                select
                                fullWidth
                                size="small"
                                value={studioPack.chrcate_id ? chargeCategoryList?.find(value => value.chrcate_id === studioPack.chrcate_id)?.chrcate_id : ""}
                                onChange={e => {
                                    if (e.target.value) {
                                        setStudioPack({
                                            ...studioPack,
                                            chrcate_id: parseInt(e.target.value),
                                        })
                                    } else {
                                        setStudioPack({
                                            ...studioPack,
                                            chrcate_id: undefined,
                                        })
                                    }
                                }}
                            >
                                <MenuItem value="" />
                                {chargeCategoryList?.map(chargeCategory => (
                                    <MenuItem
                                        key={chargeCategory.chrcate_id}
                                        value={chargeCategory.chrcate_id}
                                    >{chargeCategory.chrcate_name}</MenuItem>
                                ))}
                            </TextField>
                        </LabelItem>
                        <LabelItem
                            title="パック料金"
                        >
                            <TextField
                                size="small"
                                sx={{ width: 90, verticalAlign: "middle" }}
                                type="number"
                                inputProps={{
                                    sx: {
                                        padding: 0.5
                                    }
                                }}
                                onChange={(e) => {
                                    setStudioPack({
                                        ...studioPack,
                                        pack_charge: Number(e.target.value)
                                    })
                                }}
                                value={studioPack.pack_charge ? studioPack.pack_charge : ""}
                            />

                            <TextField
                                select
                                sx={{ width: 150, verticalAlign: "middle" }}
                                size="small"
                                inputProps={{
                                    sx: {
                                        padding: 0.5
                                    }
                                }}
                                value={studioPack.pack_charge_type ? studioPack.pack_charge_type : ""}
                                onChange={e => {
                                    if (e.target.value) {
                                        setStudioPack({
                                            ...studioPack,
                                            pack_charge_type: e.target.value as PackChargeType,
                                        })
                                    } else {
                                        setStudioPack({
                                            ...studioPack,
                                            pack_charge_type: undefined,
                                        })
                                    }
                                }}
                            >
                                {Object.keys(PackChargeType).map(key => (
                                    <MenuItem
                                        key={key}
                                        value={key}
                                    >{PackChargeTypeStrings[key as PackChargeType]}</MenuItem>
                                ))}
                            </TextField>
                        </LabelItem>
                        <LabelItem
                            title="適用優先度"
                        >
                            <TextField
                                size="small"
                                sx={{ width: 90, verticalAlign: "middle" }}
                                type="number"
                                inputProps={{
                                    sx: {
                                        padding: 0.5
                                    }
                                }}
                                onChange={(e) => {
                                    setStudioPack({
                                        ...studioPack,
                                        priority: Number(e.target.value)
                                    })
                                }}
                                value={studioPack.priority ? studioPack.priority : ""}
                            />
                        </LabelItem>
                    </Stack>
                    <Stack
                        spacing={1}
                        direction="row"
                        justifyContent="right"
                        sx={{
                            mt: 3
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={() => {
                                doSave()
                            }}
                        >保存</Button>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                onClose(false)
                            }}
                        >キャンセル</Button>
                    </Stack>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

export default StudioPackEditDialog